import { useEffect, useRef, useState } from 'react';
import { Send, Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { emailAPI, commsFilesAPI, errMsg, notifyCommsChanged, splitAddrs } from '../../lib/communicationsAPI';
import { RichTextEditor } from './RichTextEditor';
import { AttachmentPicker } from './Attachments';

const MODES = [['reply', 'Reply'], ['reply_all', 'Reply all'], ['forward', 'Forward'], ['new', 'New email']];
const join = (a) => (a || []).join(', ');

const initial = (conv, client, mailboxes, defaults) => {
  const own = mailboxes.find((m) => m.id === conv?.communication_account_id);
  const isEmail = conv?.channel === 'email';
  return {
    mailbox_id: (own || mailboxes.find((m) => m.is_default) || mailboxes[0] || {}).id || '',
    mode: isEmail ? 'reply' : 'new',
    to: defaults?.to ?? (isEmail ? (conv.counterpart_email || '') : (client?.email || '')),
    cc: '', bcc: '',
    subject: defaults?.subject ?? (conv ? (isEmail && !/^re:/i.test(conv.subject || '') ? `Re: ${conv.subject}` : conv.subject || '') : ''),
  };
};

// Email composer: mailbox picker, reply / reply-all / forward / new, To/CC/BCC, subject, TipTap body,
// attachments and draft autosave (synced to the provider's Drafts when a mailbox is connected).
export const EmailComposer = ({ convId, conv, client, mailboxes, clientId, defaults, onSent, toolbar }) => {
  const [f, setF] = useState(() => initial(conv, client, mailboxes, defaults));
  const [showCc, setShowCc] = useState(false);
  const [html, setHtml] = useState('');
  const [atts, setAtts] = useState([]);
  const [sending, setSending] = useState(false);
  const [saved, setSaved] = useState(null);
  const editorRef = useRef(null);
  const timer = useRef(null);
  const mb = mailboxes.find((m) => m.id === f.mailbox_id);
  const isEmailConv = conv?.channel === 'email';

  useEffect(() => {
    if (!convId) return undefined;
    commsFilesAPI.getDraft(convId).then((r) => {
      const d = r.data.draft;
      if (d?.channel === 'email' && d.email) {
        setF((x) => ({ ...x, ...d.email, to: join(d.email.to), cc: join(d.email.cc), bcc: join(d.email.bcc), mailbox_id: d.email.mailbox_id || x.mailbox_id }));
        if (d.email.cc?.length || d.email.bcc?.length) setShowCc(true);
        loadedBody.current = d.body; editorRef.current?.setHtml(d.body); setHtml(d.body); setSaved(d.updated_at); hadDraft.current = true;
      }
    }).catch(() => {});
    return () => clearTimeout(timer.current);
  }, [convId]);

  const hadDraft = useRef(false);
  const loadedBody = useRef('');
  const touched = useRef(false);
  const payload = (body = html) => ({ ...f, to: splitAddrs(f.to), cc: splitAddrs(f.cc), bcc: splitAddrs(f.bcc), body });
  const queueSave = (nextF, body) => {
    if (!convId || !touched.current) return;
    const empty = !(body || '').replace(/<[^>]*>/g, '').trim() && !nextF.subject && !nextF.to && !nextF.cc && !nextF.bcc;
    if (empty && !hadDraft.current) return; // never autosave an untouched/empty email draft
    clearTimeout(timer.current);
    timer.current = setTimeout(() => emailAPI.saveDraft(convId, { ...nextF, to: splitAddrs(nextF.to), cc: splitAddrs(nextF.cc), bcc: splitAddrs(nextF.bcc), body })
      .then((r) => { hadDraft.current = true; setSaved(r.data.draft?.updated_at); }).catch(() => {}), 2000);
  };
  const set = (k, v) => { touched.current = true; const n = { ...f, [k]: v }; setF(n); queueSave(n, html); };
  const onHtml = (v) => { if (v !== html && v !== loadedBody.current) touched.current = true; setHtml(v); queueSave(f, v); };

  const send = async () => {
    if (!html.trim() && !atts.length) return;
    setSending(true); clearTimeout(timer.current);
    try {
      const data = { ...payload(), attachment_ids: atts.map((a) => a.id) };
      const r = convId ? await emailAPI.send(convId, data) : await emailAPI.sendNew({ ...data, client_id: clientId, mode: 'new' });
      editorRef.current?.clear(); setHtml(''); setAtts([]); setSaved(null);
      toast.success(`Email sent from ${mb?.address}`);
      notifyCommsChanged(); onSent?.(r.data.conversation_id);
    } catch (e) { toast.error(errMsg(e, 'Failed to send email')); } finally { setSending(false); }
  };

  if (!mailboxes.length) return <p className="text-sm text-muted-foreground p-3" data-testid="email-composer-no-mailbox">Connect a mailbox in Settings → Communications to send email.</p>;
  return (
    <div className="space-y-2" data-testid="email-composer">
      <div className="flex flex-wrap items-center gap-2">
        {toolbar}
        {isEmailConv && (
          <Select value={f.mode} onValueChange={(v) => set('mode', v)}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid="email-mode-select"><SelectValue /></SelectTrigger>
            <SelectContent>{MODES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <Select value={f.mailbox_id} onValueChange={(v) => set('mailbox_id', v)}>
          <SelectTrigger className="h-8 w-56 text-xs" data-testid="email-mailbox-select" aria-label="Send from"><SelectValue /></SelectTrigger>
          <SelectContent>{mailboxes.map((m) => <SelectItem key={m.id} value={m.id}>{m.label ? `${m.label} · ` : ''}{m.address}</SelectItem>)}</SelectContent>
        </Select>
        <span className="ml-auto text-[11px] font-medium px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-900 dark:bg-cyan-900/40 dark:text-cyan-100 flex items-center gap-1" data-testid="email-channel-indicator">
          <Mail className="w-3 h-3" />Sending via Email from {mb?.address}
        </span>
      </div>
      <div className="grid gap-1.5">
        <div className="flex items-center gap-2"><span className="text-xs w-12 text-muted-foreground">To</span>
          <Input className="h-8 text-sm" value={f.to} onChange={(e) => set('to', e.target.value)} placeholder={f.mode.startsWith('reply') ? 'Defaults to the sender' : 'name@example.com'} data-testid="email-to-input" />
          {!showCc && <button type="button" className="text-xs text-primary" onClick={() => setShowCc(true)} data-testid="email-show-cc">Cc/Bcc</button>}</div>
        {showCc && <>
          <div className="flex items-center gap-2"><span className="text-xs w-12 text-muted-foreground">Cc</span><Input className="h-8 text-sm" value={f.cc} onChange={(e) => set('cc', e.target.value)} data-testid="email-cc-input" /></div>
          <div className="flex items-center gap-2"><span className="text-xs w-12 text-muted-foreground">Bcc</span><Input className="h-8 text-sm" value={f.bcc} onChange={(e) => set('bcc', e.target.value)} data-testid="email-bcc-input" /></div>
        </>}
        <div className="flex items-center gap-2"><span className="text-xs w-12 text-muted-foreground">Subject</span><Input className="h-8 text-sm" value={f.subject} onChange={(e) => set('subject', e.target.value)} data-testid="email-subject-input" /></div>
      </div>
      <RichTextEditor ref={editorRef} onChange={onHtml} onSubmit={send} placeholder="Write your email… (the firm signature is added automatically)" testId="email-body-input" />
      <div className="flex items-center gap-2">
        <AttachmentPicker value={atts} onChange={setAtts} conversationId={convId} />
        <span className="text-[11px] text-muted-foreground" data-testid="email-draft-status">{saved ? 'Draft saved' : ''}</span>
        <Button size="sm" className="ml-auto" onClick={send} disabled={sending || (!html.trim() && !atts.length)} data-testid="email-send-btn">
          {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}Send email
        </Button>
      </div>
    </div>
  );
};
